import { FormEvent, useEffect, useState } from "react";

import { createBankApi, getBanksApi, updateBankApi } from "../api/api";
import { useAuth } from "../context/AuthContext";

type BankItem = {
  id: number;
  bankName: string;
  ifscCode: string;
  status: boolean;
};

type BankFormState = {
  bankName: string;
  ifscCode: string;
  status: boolean;
};

const emptyForm = (): BankFormState => ({
  bankName: "",
  ifscCode: "",
  status: true,
});

const normalizeBank = (item: any): BankItem => ({
  id: Number(item?.bank_id ?? item?.id ?? item?.bankId ?? item?.banks_id ?? item?.bank_id_pk ?? 0),
  bankName: item?.bank_name ?? item?.bankName ?? item?.name ?? "",
  ifscCode: item?.ifsc_code ?? item?.ifscCode ?? item?.ifsc ?? "",
  status: item?.status !== undefined && item?.status !== null ? Number(item.status) === 1 : true,
});

const extractBankList = (value: any): any[] => {
  if (Array.isArray(value)) {
    return value;
  }

  if (!value || typeof value !== "object") {
    return [];
  }

  const candidates = [value.data, value.banks, value.items, value.rows, value.list, value.result, value.records];
  for (const candidate of candidates) {
    const extracted = extractBankList(candidate);
    if (extracted.length > 0) {
      return extracted;
    }
  }

  return [];
};

export default function AddBankPage() {
  const { user, pendingLogin, showToast } = useAuth();
  const [banks, setBanks] = useState<BankItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingBank, setEditingBank] = useState<BankItem | null>(null);
  const [form, setForm] = useState<BankFormState>(emptyForm());

  const fetchBanks = async () => {
    setLoading(true);
    try {
      const authToken =
        user?.token ||
        (user as any)?.accessToken ||
        pendingLogin?.accessToken ||
        pendingLogin?.user?.token ||
        "";

      const response = await getBanksApi(authToken);
      const payload = response?.data ?? response;
      const list = extractBankList(payload);
      setBanks(list.map(normalizeBank));
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Failed to load banks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user || pendingLogin) {
      fetchBanks();
    }
  }, [user?.token, pendingLogin?.accessToken]);

  const resetForm = () => {
    setForm(emptyForm());
  };

  const openAddModal = () => {
    setEditingBank(null);
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (bank: BankItem) => {
    setEditingBank(bank);
    setForm({
      bankName: bank.bankName,
      ifscCode: bank.ifscCode,
      status: bank.status,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBank(null);
    resetForm();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();

    if (!form.bankName.trim() || !form.ifscCode.trim()) {
      showToast("Please fill bank name and IFSC code");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        login_id: user?.id ?? 1,
        bank_name: form.bankName.trim(),
        ifsc_code: form.ifscCode.trim(),
        status: form.status ? 1 : 0,
      };

      if (editingBank) {
        const response = await updateBankApi(editingBank.id, payload, user?.token);
        const updatedBank = normalizeBank(response?.data ?? response ?? { ...payload, id: editingBank.id });
        setBanks((prevBanks) =>
          prevBanks.map((bank) => (bank.id === editingBank.id ? { ...bank, ...updatedBank } : bank))
        );
        showToast("Bank updated successfully");
      } else {
        const response = await createBankApi(payload, user?.token);
        const createdBank = normalizeBank(response?.data ?? response ?? { ...payload, id: Date.now() });
        setBanks((prevBanks) => [createdBank, ...prevBanks]);
        showToast("Bank added successfully");
      }

      closeModal();
      await fetchBanks();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Failed to save bank");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteBank = (id: number) => {
    setBanks((prevBanks) => prevBanks.filter((bank) => bank.id !== id));
  };

  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <h1>Bank List</h1>
          <p style={{ margin: "4px 0 0", color: "#6b7280" }}>Manage your saved bank accounts</p>
        </div>
        <button className="btn btn-primary" type="button" onClick={openAddModal}>
          + Add Bank
        </button>
      </div>

      <div className="card" style={{ display: "grid", gap: 16 }}>
        {loading ? (
          <div style={{ padding: 20, textAlign: "center", color: "#6b7280" }}>Loading banks…</div>
        ) : banks.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: "#6b7280" }}>
            No bank account added yet.
          </div>
        ) : (
          banks.map((bank) => (
            <div
              key={bank.id}
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 16,
                padding: 16,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>{bank.bankName}</div>
                <div style={{ marginTop: 6, color: "#4b5563" }}>IFSC: {bank.ifscCode || "—"}</div>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn" type="button" onClick={() => openEditModal(bank)}>
                  Edit
                </button>
                <button className="btn btn-danger" type="button" onClick={() => deleteBank(bank.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 1000,
          }}
        >
          <div style={{ background: "white", borderRadius: 16, width: "100%", maxWidth: 480, padding: 24 }}>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>
              {editingBank ? "Edit Bank" : "Add Bank"}
            </div>

            <form onSubmit={submit} className="form-grid">
              <input
                placeholder="Bank Name"
                value={form.bankName}
                onChange={(e) => setForm({ ...form, bankName: e.target.value })}
              />

              <input
                placeholder="IFSC Code"
                value={form.ifscCode}
                onChange={(e) => setForm({ ...form, ifscCode: e.target.value })}
              />

              <label style={{ display: "flex", alignItems: "center", gap: 8, color: "#374151" }}>
                <input
                  type="checkbox"
                  checked={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.checked })}
                />
                Active
              </label>

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button className="btn" type="button" onClick={closeModal}>
                  Cancel
                </button>
                <button className="btn btn-primary" type="submit" disabled={submitting}>
                  {submitting ? "Saving..." : editingBank ? "Update Bank" : "Save Bank"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
